import {useMutation, useQuery} from '@apollo/client';
import * as React from 'react';

import {
  ProgramClass,
  horaCurta,
  lotacao,
  ordenarPorHorario,
  podeCancelarCheckin,
  podeFazerCheckin,
  resolveTimeZone,
  statusDaAula,
} from '../scheduleDay';
import {
  MUTATION_STUDENT_CHECKIN,
  MUTATION_STUDENT_DELETE_CHECKIN,
  QUERY_STUDENT_SCHEDULE,
} from '../studentQueries';
import {StudentTheme} from '../studentTheme';
import {addDays, dayLabel, toDateParam} from '../wodsDay';

interface Props {
  theme: StudentTheme;
}

interface ScheduleData {
  aulasPorDia?: {programClasses?: ProgramClass[] | null} | null;
}

const CORES: Record<string, keyof StudentTheme> = {
  positivo: 'primary',
  bloqueado: 'neutral',
  neutro: 'neutral',
};

export const ScheduleScreen: React.FC<Props> = ({theme}) => {
  const [dia, setDia] = React.useState(() => new Date());

  // Resolvido uma vez: nao muda durante a sessao e evita variavel nova a cada
  // render, que refaria a query.
  const tz = React.useMemo(() => resolveTimeZone(), []);

  const {data, loading, error, refetch} = useQuery<ScheduleData>(QUERY_STUDENT_SCHEDULE, {
    variables: {date: toDateParam(dia), tz},
  });

  const aulas = React.useMemo(
    () => ordenarPorHorario(data?.aulasPorDia?.programClasses ?? []),
    [data?.aulasPorDia?.programClasses],
  );

  // Guarda qual aula esta em acao para desabilitar so o botao dela, e nao a
  // agenda inteira.
  const [emAcao, setEmAcao] = React.useState<string | null>(null);
  const [falha, setFalha] = React.useState<string | null>(null);

  const [fazerCheckin] = useMutation(MUTATION_STUDENT_CHECKIN);
  const [cancelarCheckin] = useMutation(MUTATION_STUDENT_DELETE_CHECKIN);

  const agir = async (aula: ProgramClass) => {
    if (emAcao) return;

    setEmAcao(aula.id);
    setFalha(null);

    try {
      if (podeCancelarCheckin(aula)) {
        await cancelarCheckin({variables: {checkinId: aula.checkin?.id}});
      } else {
        await fazerCheckin({variables: {programClassId: aula.id}});
      }

      // O backend recalcula canCheckin e a lotacao; refazer a query e mais
      // seguro que adivinhar o novo estado no cliente.
      await refetch();
    } catch (erro) {
      // A mutation devolve mensagem util ("Class is full", limite de checkins);
      // engolir isso deixaria o aluno sem saber por que nao entrou.
      const mensagem = erro instanceof Error ? erro.message : 'Não foi possível concluir.';
      setFalha(mensagem);
    } finally {
      setEmAcao(null);
    }
  };

  return (
    <div className="agenda">
      <nav className="wods__dias" style={{borderColor: theme.border}}>
        <button
          type="button"
          className="wods__seta"
          onClick={() => setDia((atual) => addDays(atual, -1))}
          aria-label="Dia anterior"
        >
          <i className="fa-light fa-chevron-left" aria-hidden="true" />
        </button>

        <span className="wods__dia-atual">{dayLabel(dia)}</span>

        <button
          type="button"
          className="wods__seta"
          onClick={() => setDia((atual) => addDays(atual, 1))}
          aria-label="Próximo dia"
        >
          <i className="fa-light fa-chevron-right" aria-hidden="true" />
        </button>
      </nav>

      {loading && <p className="student-screen__estado">Carregando…</p>}

      {error && !loading && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar a agenda.</p>
          <button type="button" className="student-app__retry" onClick={() => refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {falha && (
        <p className="student-screen__estado" style={{color: theme.neutral}}>
          {falha}
        </p>
      )}

      {!loading && !error && aulas.length === 0 && (
        <p className="student-screen__estado">Nenhuma aula neste dia.</p>
      )}

      {!loading &&
        !error &&
        aulas.map((aula) => {
          const status = statusDaAula(aula.canCheckin);

          return (
            <article
              key={aula.id}
              className="aula-card"
              style={{backgroundColor: theme.body, borderColor: theme.border}}
            >
              <div className="aula-card__horario" style={{color: theme.primary}}>
                <strong>{horaCurta(aula.startTime)}</strong>
                <span style={{color: theme.neutral}}>{horaCurta(aula.endTime)}</span>
              </div>

              <div className="aula-card__info">
                <h2 className="aula-card__programa">{aula.program?.name}</h2>

                <p className="aula-card__local" style={{color: theme.neutral}}>
                  {[aula.filial?.name, aula.coach?.name].filter(Boolean).join(' · ')}
                </p>

                {status.label && (
                  <span className="aula-card__status" style={{color: theme[CORES[status.tom]] as string}}>
                    {status.label}
                  </span>
                )}
              </div>

              <div className="aula-card__acao">
                <span className="aula-card__lotacao" style={{color: theme.neutral}}>
                  <i className="fa-light fa-user" aria-hidden="true" />
                  {lotacao(aula)}
                </span>

                {(podeFazerCheckin(aula) || podeCancelarCheckin(aula)) && (
                  <button
                    type="button"
                    className="aula-card__botao"
                    disabled={emAcao === aula.id}
                    style={
                      podeCancelarCheckin(aula)
                        ? {borderColor: theme.border, color: theme.neutral}
                        : {backgroundColor: theme.primary, color: theme.primaryText, borderColor: 'transparent'}
                    }
                    onClick={() => agir(aula)}
                  >
                    {emAcao === aula.id ? '…' : podeCancelarCheckin(aula) ? 'Cancelar' : 'Check-in'}
                  </button>
                )}
              </div>
            </article>
          );
        })}
    </div>
  );
};
